/**
 * ダウンロード時の画質選択ダイアログ
 *
 * 選んだ時点ですぐに閉じ、変換待ちとダウンロードは呼び出し側（ダウンロードボタン）に任せる。
 */
import { Loader2 } from 'lucide-react'

import { QualityOptionList } from '@/components/shared/quality-option-list'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useVideoQuality } from '@/hooks/useVideoQuality'
import { useUISettingsStore } from '@/store/useUISettingsStore'

type Props = {
  episodeId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 画質が選ばれた時点で呼ばれる */
  onConfirm: (quality: string) => void
}

export const DownloadQualityDialog = ({ episodeId, open, onOpenChange, onConfirm }: Props) => {
  // 閉じている間は取得しない
  const { options, isLoading } = useVideoQuality(open ? episodeId : null)
  const defaultQuality = useUISettingsStore((s) => s.downloadQuality)
  const setDefaultQuality = useUISettingsStore((s) => s.setDownloadQuality)

  const handleSelect = (quality: string) => {
    setDefaultQuality(quality)
    onOpenChange(false)
    onConfirm(quality)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>画質を選択</DialogTitle>
          <DialogDescription>
            未生成の画質は変換してからダウンロードします。進捗はダウンロードボタンに表示されます。
          </DialogDescription>
        </DialogHeader>

        {isLoading && options.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            画質を確認しています...
          </div>
        ) : options.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">この動画は画質を選択できません</p>
        ) : (
          <QualityOptionList options={options} selectedQuality={defaultQuality} onSelect={handleSelect} />
        )}
      </DialogContent>
    </Dialog>
  )
}
